/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import { Component } from "react";

import Banner from "../../components/banner";
import AddToCartButton from "../../components/buttons/add-to-cart-button";
import Certifications from "../../components/certifications";
import InStock from "../../components/in-stock";
import Newsletter from "../../components/newsletter";
import RelatedProducts from "../../components/related-products";
import { Slideshow } from "../../components/slideshow";
import CollectionKind from "../../enums/collection-kind";
import ProductKind from "../../enums/product-kind";
import { toAbsolutePageURL } from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class BlissAromatherapyCauseCandle extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render(): JSX.Element {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/products/bliss-aromatherapy-cause-candle",
                                           market);
    const alt = "Lasting Smiles";
    const title = "Bliss - Aromatherapy Cause Candle · Lasting Smiles";
    const description = "Aromatherapy Scented Cause Candle by Lasting Smiles." +
      "Poured with an all-natural soy coconut wax blend for an eco-friendly clean burn." +
      "Features a cotton wick and fine perfume oils infused with essential oils." +
      "Lasting Smiles candles are vegan, cruelty-free, phthalate-free, and petroleum free." +
      "Our premium candles are proudly poured in the USA." +
      "With a modern design, this candle fits any home decor and is perfect for gifting." +
      "Each Lasting Smiles Cause Candle shines light on causes that matter and helps support and fund diverse causes caring for people and the planet.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/products/bliss-aromatherapy-cause-candle", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/bliss-aromatherapy-cause-candle", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/bliss-aromatherapy-cause-candle", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products", market),
                    "name": "Products",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products/bliss-aromatherapy-cause-candle", market),
                    "name": "Bliss - Aromatherapy Cause Candle",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif text-3xl text-center">
              Bliss - Aromatherapy Cause&nbsp;Candle
            </h1>
            <p className="text-center text-xl">
              Adding elevated warmth to any environment
            </p>
            <div className="flex flex-col sm:flex-row">
              <div className="flex-initial sm:max-w-xs">
                <Slideshow
                  imageAlts={[
                    "Bliss - Aromatherapy Cause Candle Box",
                    "Bliss - Aromatherapy Cause Candle",
                    "Bliss - Aromatherapy Cause Candle Life 1",
                    "Bliss - Aromatherapy Cause Candle Life 2",
                  ]}
                  imageJpegs={[
                    `${require("../../public/images/products/bliss-aromatherapy-cause-candle/box.jpg")}`,
                    `${require("../../public/images/products/bliss-aromatherapy-cause-candle/single.jpg")}`,
                    `${require("../../public/images/products/bliss-aromatherapy-cause-candle/life1.jpg")}`,
                    `${require("../../public/images/products/bliss-aromatherapy-cause-candle/life2.jpg")}`,
                  ]}
                  imageWebps={[
                    `${require("../../public/images/products/bliss-aromatherapy-cause-candle/box.webp")}`,
                    `${require("../../public/images/products/bliss-aromatherapy-cause-candle/single.webp")}`,
                    `${require("../../public/images/products/bliss-aromatherapy-cause-candle/life1.webp")}`,
                    `${require("../../public/images/products/bliss-aromatherapy-cause-candle/life2.webp")}`,
                  ]}
                />
              </div>
              <div className="flex-1 m-5">
                <p className="sm:text-left text-center">
                  Poured with an all-natural soy coconut wax blend for an eco-friendly clean burn. Features a cotton wick and fine perfume oils infused with essential oils. Lasting Smiles candles are vegan, cruelty-free, phthalate-free, and petroleum free.
                  Our premium candles are proudly poured in the USA.
                  With a modern design, this candle fits any home decor and is perfect for gifting.
                  Each Lasting Smiles Cause Candle shines light on causes that matter and helps support and fund diverse causes caring for people and the planet.
                </p>
                <p className="my-5">
                  <strong>Top:</strong> Grapefruit, Peach, Green pear
                  <br />
                  <strong>Middle:</strong> Lemon zest, Mangosteen, Red currants
                  <br />
                  <strong>Base:</strong> Pineapple, Caramel, Apple
                </p>
                <p className="my-5">
                  Net Wt. 9.7 oz (275 grams)
                  <br />
                  Burn Time: Up to 65 hours
                </p>
                <InStock />
                <AddToCartButton
                  collection={CollectionKind.Candle}
                  product={ProductKind.BlissCandle}
                />
                <Certifications collection={CollectionKind.Candle} />
              </div>
            </div>
          </div>
        </section>
        <section className="bg-bliss-300 px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <div className="text-center">
              <h2 className="font-medium font-serif text-2xl">
                Candle Safety Instructions
              </h2>
            </div>
            <div className="flex flex-col flex-wrap sm:flex-row">
              <div className="flex-1 m-5">
                <h3 className="font-medium font-serif sm:text-left text-center text-xl">
                  Product Care Instructions
                </h3>
                <p className="sm:text-left text-center text-lg">
                  <strong>Warning: </strong>
                  Burn candle within sight.
                  Keep away from things that catch fire.
                  Keep away from draft and vibrations.
                  Keep away from children and pets.
                </p>
              </div>
              <div className="flex-1 m-5">
                <h3 className="font-medium font-serif sm:text-left text-center text-xl">
                  How to Use Product
                </h3>
                <p className="sm:text-left text-center text-lg">
                  <strong>Directions: </strong>
                  Trim wick 1/4 inch before lighting.
                  Keep candle free of any foreign materials including matches and wick trimmings.
                  Only burn the candle on a level, fire resistant surface.
                  Do not burn the candle for more than 4 hours at a time.
                  Discontinue use when 1/2" (6mm) of wax remains on the bottom.
                </p>
              </div>
            </div>
          </div>
        </section>
        <RelatedProducts
          collection={CollectionKind.Candle}
          skipProduct={ProductKind.BlissCandle}
        />
      </>
    );
  }
}
